/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import "./index.css";
import { Provider } from "react-redux";
import { Toaster } from "sonner";
import { TailwindIndicator } from "@/components/tailwind-indicator";
import { SocketProvider } from "@/hooks/use-socket.tsx";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { store } from "@/redux/store";
import ErrorBoundary from "@/components/ErrorBoundary";
import "./i18n";
import App from "./App";
import { loadProductIdConfig } from "@/services/productIdConfig";
// N-5 (S697): does this build hold the identifiers of the Stripe account it is
// pointed at? Reported, never fatal.
import { verifyProductIdsAgainstBackend } from "@/config/product-id-verification";
import { NetworkRecoveryBanner } from "@/components/common/network-recovery-banner";
import { deliveredSession } from "@/config/session";
import { sentryOptions } from "@/config/sentry-options";
import { installChunkFailureRecovery } from "@/lib/chunk-recovery";

const isSecureEnv =
  import.meta.env.VITE_REACT_ENV === "production" ||
  import.meta.env.VITE_REACT_ENV === "staging";

if (isSecureEnv && import.meta.env.VITE_REACT_SENTRY_DSN) {
  // Session 853 review, N-1: error reports only -- no page-speed tracing and
  // no session replays, which the privacy policy allows only after consent.
  Sentry.init(
    sentryOptions(
      import.meta.env.VITE_REACT_SENTRY_DSN,
      import.meta.env.VITE_REACT_ENV,
    ),
  );
  // FCR S732: every event says which delivered session produced it.
  const session = deliveredSession();
  if (session) Sentry.setTag("deskshare_session", session);
}

// Load the canonical QLM<->Stripe product-id maps from the backend (P-1).
// Non-blocking: the baked-in constants are the synchronous fallback, so render
// proceeds immediately and getters return correct values either way.
// N-5 (S697): and check, once per page load, that the Stripe product
// identifiers this build holds are identifiers the back end knows about.
//
// config/product-ids.ts falls back to the PRODUCTION identifier whenever its
// build-time variable is unset, so an environment pointed at a Stripe test
// account and built without those variables holds production identifiers while
// its invoices carry test ones. Every lookup keyed on an identifier then misses
// at once, and every miss is silent -- indistinguishable from "this customer
// owns nothing".
//
// Sequenced after loadProductIdConfig() so the two share the browser's
// connection rather than racing for it, and awaited nowhere: this must not be
// able to delay or prevent the first render. It reports and returns.
void loadProductIdConfig().then(() => verifyProductIdsAgainstBackend());

// ST-6 (FCR S733): a page part that failed to download while the connection
// was down is recovered by one reload (lib/chunk-recovery.ts), instead of the
// page staying broken until someone reloads a window that has no reload button.
installChunkFailureRecovery();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <Provider store={store}>
        <CurrencyProvider>
          <SocketProvider>
            <App />
            {/* UX S540: richColors gives semantic colour (green=success,
                red=error, yellow=warning). closeButton lets users dismiss
                toasts on a slow connection. position="bottom-right" keeps
                toasts away from the checkout form (bottom-left on mobile
                would overlap the summary panel). duration=5000 gives
                enough time to read multi-line toasts without being annoying. */}
            <Toaster
              position="bottom-right"
              richColors
              closeButton
              duration={5000}
            />
            <TailwindIndicator />
            {/* A-2 / W-3 (S711 review): the banner, connected to recovery. */}
            <NetworkRecoveryBanner />
          </SocketProvider>
        </CurrencyProvider>
      </Provider>
    </ErrorBoundary>
  </StrictMode>,
);
